import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { EventLocation } from "../select-event-location-dialog/select-event-location-dialog.component";
import { LogService } from "../../service/logging/log.service";

interface NoDialog {
  kind: "none";
}

interface AlertDialog {
  kind: "alert";
  title: string;
  subtitle: string;
  isError: boolean;
}

interface ConfirmDialog {
  kind: "confirm";
  title: string;
  subtitle: string;
}

interface SelectEventLocationDialog {
  kind: "location";
  locations: EventLocation[];
}

interface SelectOneRadio {
  kind: "selectOneRadio";
  title: string;
  subtitle: string;
  cancelFunction: () => void,
  possibilities: string[]; //Maybe turn into disctionary to allow values to be something different than shown
}

type Dialog =
  | AlertDialog
  | ConfirmDialog
  | NoDialog
  | SelectEventLocationDialog
  | SelectOneRadio;
type PendingDialog = [Subject<{}>, Dialog];

// TODO: Use this service for all dialog usage

@Injectable({ providedIn: 'root' })
export class DialogService {
  dialogs: PendingDialog[] = [];
  currentDialogSubject = new BehaviorSubject<Dialog>({kind: 'none'});
  currentDialog$ = this.currentDialogSubject.asObservable();
  logger = inject(LogService);

  constructor () {
    this.logger.debug("dialog-service instance: ", this)
  }

  dispatch(dialog: PendingDialog): void {
    this.currentDialogSubject.next(dialog[1]);
  }

  clearDialogs(): void {
    this.currentDialogSubject.next({ kind: "none" });
  }

  addDialog(dialog: PendingDialog): void {
    const wasEmpty = this.dialogs.length === 0;

    this.dialogs.push(dialog);

    if (wasEmpty) this.dispatch(dialog);
  }

  private runNextOrClear(): void {
    if (this.dialogs.length) this.dispatch(this.dialogs[0]);
    else this.clearDialogs();
  }

  alert(title: string, subtitle?: string, isError?: boolean): Observable<{}> {
    const rval = new Subject();

    this.addDialog([
      rval,
      {
        kind: "alert",
        title: title,
        subtitle: subtitle,
        isError: isError,
      },
    ]);
    return rval;
  }

  confirm(title: string, subtitle?: string): Observable<{}> {
    const rval = new Subject();

    this.addDialog([
      rval,
      {
        kind: "confirm",
        title: title,
        subtitle: subtitle,
      },
    ]);

    return rval;
  }

  dequeueDialog(result: boolean): void {
    const [subject, _] = this.dialogs.shift();

    if (result) subject.next(null);

    subject.complete();
    this.runNextOrClear();
  }

  dequeueConfirmDialog(result: boolean): void {
    const [subject, _] = this.dialogs.shift();

    subject.next(result);
    // else subject.error("Dialog was cancelled");

    subject.complete();
    this.runNextOrClear();
  }

  selectEventLocation(which: EventLocation[]): Observable<number> {
    const rval = new Subject<number>();

    this.addDialog([
      rval,
      {
        kind: "location",
        locations: which,
      },
    ]);

    return rval;
  }

  selectOneRadio(title: string, subtitle: string, which: string[], cancelFunction: () => void): Observable<string> {
    const rval = new Subject<string>();

    this.addDialog([
      rval,
      {
        kind: "selectOneRadio",
        title: title,
        subtitle: subtitle,
        possibilities: which,
        cancelFunction: cancelFunction,
      },
    ]);

    return rval;
  }

  dequeueSelectLocationDialog(result: number) {
    const [subject, _] = this.dialogs.shift();

    if (result > 0) subject.next(result);
    // else subject.error("Dialog was cancelled");

    subject.complete();
    this.runNextOrClear();
  }
}
